<!--
Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->
<div #activecolor class="dummy-color-getter"></div>
<div #defaultcolor class="default-color"></div>
@if (show) {
    <header
        [@active]="{ value: active, params: { active_color: activeColor, default_color: defaultColor } }"
        fxLayout="row"
        fxLayoutAlign="start center"
        class="appbar default-color mat-elevation-z4"
        [class.transparent-bar]="transparent"
        [class.active]="active"
        nvFocusBack
        [enableFocusBack]="!isNavFocused"
        [attr.aria-label]="ariaLabel | translate"
    >
        <div fxFlex="row" fxLayoutAlign="start center" class="nv-branding">
            <mat-icon
                [@logoSlide]="{
                    value: logoState,
                    params: { translate3d_value: logoSlideTranslate3dValue }
                }"
                (@logoSlide.done)="onLogoSlide($event)"
                svgIcon="gfnBranding"
                tabindex="-1"
                fxFlex
            >
            </mat-icon>
        </div>
        <div
            fxLayout="row"
            fxLayoutAlign="start center"
            fxFlex="1 1 auto"
            class="nav-container"
            [class.shrink-navigation]="shrinkNavigation"
        >
            @if (navButton?.id) {
                <button
                    #navButtonRef
                    mat-button
                    [attr.aria-label]="navButton.tooltip | translate"
                    [attr.aria-expanded]="navButton.id === 'menu' ? isSidebarOpened : null"
                    nvDisableTooltipIfNeeded
                    matTooltip="{{ navButton.tooltip | translate }}"
                    matTooltipClass="mat-tooltip-font-body3"
                    class="nav-button item-empty hig-button-icon font-body1"
                    fxLayout="row"
                    fxLayoutAlign="center center"
                    (click)="navClicked(navButton.id)"
                    (focus)="navFocused()"
                    (blur)="navBlurred()"
                    cdkFocusInitial
                >
                    <!--
          set mat-icon's overflow to clip to avoid its content height exceed
          the bounding rect and causes the mat-icon being recognized as a
          scroll container
          -->
                    <mat-icon
                        [style.overflowX]="'clip'"
                        [style.overflowY]="'clip'"
                        [svgIcon]="navButton.id"
                        [ngClass]="{ 'flip-rtl-element': navButton.id === 'arrow_left' }"
                    ></mat-icon>
                </button>
            }
            <div
                [fxHide]="shrinkNavigation"
                [nvHeadingFocusable]="1"
                class="title font-body1 toolbar-title-text"
                fxFlex="1 1 auto"
            >
                {{ title | translate: titleParams }}
            </div>
            <!-- Live region always in DOM (not behind *ngIf) so the screen reader
      detects the empty-to-text transition on the very first activation. -->
            <span class="cdk-visually-hidden" role="status" aria-live="polite" aria-atomic="true">
                {{ active ? (title | translate: titleParams) : '' }}
            </span>
            @for (icon of icons; track iconTrack($index, icon)) {
                @if (icon.icon && !icon.end && !icon.loginData) {
                    <button
                        mat-button
                        [@icon]
                        [nvTooltipDisabled]="!icon.tooltip"
                        [matTooltip]="icon.tooltip | translate"
                        matTooltipClass="nv-oogg oogg-tooltip"
                        [attr.aria-label]="icon.tooltip | translate"
                        class="app-bar-icons-left font-body1 hig-button-icon"
                        fxLayout="row"
                        fxLayoutAlign="center center"
                        fxFlex="none"
                        (click)="iconClicked(icon)"
                    >
                        <mat-icon [svgIcon]="icon.icon"></mat-icon>
                    </button>
                }
            }
            <div fxFlex></div>
            <div class="title font-body1">{{ info | translate: infoParams }}</div>
        </div>
        <div fxFlex="1 1 100%">
            <ng-content></ng-content>
        </div>
        @for (icon of icons; track iconTrack($index, icon)) {
            @if (((icon.icon && icon.end) || icon.button) && !icon.loginData) {
                <button
                    mat-button
                    [@icon]
                    [nvTooltipDisabled]="!icon.tooltip"
                    [matTooltip]="icon.tooltip | translate"
                    matTooltipClass="nv-oogg oogg-tooltip"
                    [attr.aria-label]="icon.tooltip | translate"
                    class="app-bar-icons font-body2 hig-button-icon"
                    [ngClass]="{
                        'app-bar-item-with-text': icon.text !== undefined,
                        'font-button2 mat-flat-button mat-accent app-bar-button-end': icon.button
                    }"
                    [nvFocusRipple]="!!icon.button"
                    [disabled]="icon.disabled"
                    fxLayout="row"
                    fxLayoutAlign="center center"
                    fxFlex="none"
                    (click)="iconClicked(icon)"
                >
                    @if (icon.icon) {
                        <mat-icon [svgIcon]="icon.icon"></mat-icon>
                    }
                    @if (icon.text) {
                        <span [ngClass]="{ 'app-bar-padding': icon.button === undefined }">{{
                            icon.text | translate
                        }}</span>
                    }
                </button>
            }
            @if (icon.loginData) {
                <button
                    mat-button
                    class="app-bar-icons font-body1 hig-button-icon app-bar-item-with-text"
                    [matMenuTriggerFor]="menu"
                    [nvTooltipDisabled]="!icon.tooltip"
                    matTooltip="{{ icon.tooltip | translate }}"
                    matTooltipClass="nv-oogg oogg-tooltip"
                    [attr.aria-label]="icon.tooltip | translate"
                    fxFlex="none"
                >
                    <div fxFlex="1 1 auto" fxLayout="row" fxLayoutAlign="start center">
                        @if (icon.loginData.avatar) {
                            <img class="avatar-img" [src]="icon.loginData.avatar" alt="" role="presentation" />
                        }
                        @if (icon.loginData.avatarSvg) {
                            <mat-icon class="avatar-img" [svgIcon]="icon.loginData.avatarSvg"></mat-icon>
                        }
                        <div class="display-name" fxLayout="column">
                            <span class="username font-body2">{{ icon.loginData.username }}</span>
                            @if (icon.loginData.currentPlanShortName) {
                                <span class="font-body3">{{ icon.loginData.currentPlanShortName }}</span>
                            }
                        </div>
                        <mat-icon class="material-icons">arrow_drop_down</mat-icon>
                    </div>
                </button>
            }
            <mat-menu #menu="matMenu" overlapTrigger="false">
                @for (menu of icon.menu; track menu) {
                    <button mat-menu-item (click)="iconMenuClicked(menu.id)">
                        {{ menu.item | translate }}
                    </button>
                }
            </mat-menu>
        }
    </header>
}
